import {
  AttributeHeader,
  AttributeValue,
  Button,
  formatDate,
  Hr,
  IconAdorner,
  Typography,
  urlReplace,
  useLoading,
} from "best-common-react";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { RouteConstants } from "../../constants/RouteConstants";
import { useAuth } from "../../contexts/AuthContext";
import { downloadFile } from "../../httpClients/ClipsApi";
import { BoxFileDTO } from "../../types/Video";
import { formatMetadataCalendarDate } from "../../utils/DateUtils";
import { formatName } from "../../utils/PersonUtils";
import EditTagsModal from "./EditTagsModal";
import VideoShareModal from "./VideoShareModal";

const MetadataContainer = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: space-between;
  flex-wrap: wrap;
`;

type MetadataItemsProps = {
  icon?: React.ReactElement;
  title?: string;
  value?: string;
};

const MetadataItem: React.FC<MetadataItemsProps> = ({ icon, title, value }) => {
  return value?.length ? (
    <div className="d-flex align-items-center">
      <div className="bcr-me-2">{icon}</div>
      <div className="d-flex flex-column">
        {title && <AttributeHeader>{title}</AttributeHeader>}
        <AttributeValue className="bcr-mb-0">{value}</AttributeValue>
      </div>
    </div>
  ) : null;
};

type MetadataProps = MetadataItemsProps & {
  link?: string;
};

const Metadata: React.FC<MetadataProps> = ({ icon, title, value, link }) => (
  <div>
    {link ? (
      <Link to={link}>
        <MetadataItem icon={icon} title={title} value={value} />
      </Link>
    ) : (
      <MetadataItem icon={icon} title={title} value={value} />
    )}
  </div>
);

type VideoDetailsProps = {
  video: BoxFileDTO;
  setVideoTags: (value: string[]) => void;
};

const videoDateFormat = (value) => (!!value ? formatDate(DateTime.fromJSDate(new Date(value)).toUTC()) : "");

const VideoDetails: React.FC<VideoDetailsProps> = ({ video, setVideoTags }) => {
  const navigate = useNavigate();
  const videoId = video.fileId;
  const { setLoading } = useLoading();
  const { isAdmin, isBocAdmin, userInfo } = useAuth();
  const [canEditMetadata, setCanEditMetadata] = useState<boolean>(false);
  const [canEditTags, setCanEditTags] = useState<boolean>(false);
  const [videoShareOpen, setVideoShareOpen] = useState<boolean>(false);
  const [editTagsOpen, setEditTagsOpen] = useState<boolean>(false);

  const { metadata } = video;
  const tags = video.tags ? video.tags : [];
  const name: string = formatName(metadata.firstName, metadata.lastName);
  const filmDate: string = formatMetadataCalendarDate(metadata.filmDate);
  const birthday: string = formatMetadataCalendarDate(metadata.birthDate);
  const heightWeight: string = (
    (metadata.height ? metadata.height : "") +
    " " +
    (metadata.weight ? metadata.weight + "lbs" : "")
  ).trim();

  const download = async () => {
    try {
      setLoading(true);
      const result = await downloadFile(videoId);
      window.open(result, "_blank");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const editMetadata = () => {
    navigate(urlReplace(RouteConstants.METADATA_EDIT, { fileId: videoId }));
  };

  useEffect(() => {
    setCanEditTags(userInfo?.org?.name && isAdmin && video.metadata && userInfo?.org?.name === video.metadata.source);
    setCanEditMetadata(
      userInfo?.org?.name && isAdmin && video.metadata && userInfo?.org?.name === video.metadata.source,
    );
  }, [userInfo, video, isAdmin]);

  return (
    <>
      <VideoShareModal videoId={videoId} open={videoShareOpen} close={() => setVideoShareOpen(false)} />
      <EditTagsModal
        existingTags={tags}
        videoId={videoId}
        open={editTagsOpen}
        close={() => setEditTagsOpen(false)}
        setVideoTags={setVideoTags}
      />
      <div>
        <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
          <Typography variant="displaySmall">{name}</Typography>
          <div className="bcr-ms-auto d-flex flex-wrap" style={{ gap: "0.5rem" }}>
            {(isBocAdmin || canEditMetadata) && (
              <Button variant="secondary" onClick={editMetadata} startAdornment={<IconAdorner iconName="fa-pencil" />}>
                Edit Metadata
              </Button>
            )}
            {(isBocAdmin || canEditTags) && (
              <Button
                variant="secondary"
                onClick={() => setEditTagsOpen(true)}
                startAdornment={<IconAdorner iconName="fa-pencil" />}
              >
                Edit Tags
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setVideoShareOpen(true)}
              startAdornment={<IconAdorner iconName="fa-share" />}
            >
              Share
            </Button>
            <Button variant="secondary" onClick={download} startAdornment={<IconAdorner iconName="fa-download" />}>
              Download
            </Button>
          </div>
        </div>
        <Hr />
        <MetadataContainer>
          <div>
            <Typography variant="headlineSmall">Player Details</Typography>
            <Metadata title="Name" value={formatName(metadata.firstName, metadata.lastName)} />
            <Metadata title="School" value={metadata.school} />
            <Metadata title="Birthday" value={birthday} />
            <Metadata title="Height/Weight" value={heightWeight} />
            <Metadata title="Position" value={metadata.position} />
            <Metadata title="Roster Name" value={metadata.rosterName} />
            <Metadata title="Middle Name" value={metadata.middleName} />
            <Metadata title="Extended Last Name" value={metadata.extendedLastName} />
            <Metadata title="Country" value={metadata.country} />
            <Metadata title="Bats" value={metadata.bats} />
            <Metadata title="Throws" value={metadata.throwsSide} />
            <Metadata title="Player Status" value={metadata.playerStatus} />
            <Metadata title="Player Id" value={metadata.playerId} />
            <Metadata title="EBIS Id" value={metadata.ebis} />
            <Metadata title="DPL Id" value={metadata.dplId} />
            <Metadata title="Eligibility" value={metadata.eligibility} />
            <Metadata title="Classification" value={metadata.classification} />
            <Metadata title="Trainer Name" value={metadata.trainerName} />
            <Metadata title="Uniform Number" value={metadata.uni} />
            <Metadata title="Org" value={metadata.org} />
            <Metadata title="Team" value={metadata.team} />
            <Metadata title="League" value={metadata.league} />
            <Metadata title="Email" value={metadata.email} />
            <Metadata title="Phone Number" value={metadata.phoneNumber} />
            <Metadata title="Home Address" value={metadata.homeAddress} />
          </div>
          <div>
            <Typography variant="headlineSmall">Video Details</Typography>
            <Metadata title="Film Date" value={filmDate} />
            <Metadata
              title="Location"
              value={metadata.city ? metadata.city + ", " + metadata.state : metadata.state ? metadata.state : ""}
            />
            <Metadata title="Source" value={metadata.source} />
            <Metadata title="Film Name" value={metadata.fileName} />
            <Metadata title="Upload Date" value={videoDateFormat(metadata.uploadDate)} />
            <Metadata title="Posted Date" value={videoDateFormat(metadata.postedDate)} />
            <Metadata title="Video By" value={metadata.videoBy} />
            <Metadata title="Last Viewed" value={videoDateFormat(metadata.lastViewed)} />
            <Metadata title="CLIPs Org" value={metadata.clipsOrg} />
            <Metadata title="Comments" value={metadata.comments} />
          </div>
          <div>
            <Typography variant="headlineSmall">Tags</Typography>
            {tags.map((tag) => (
              <Metadata key={tag} icon={<IconAdorner iconName="fa-tag" />} value={tag} link={`/search?&tags=${tag}`} />
            ))}
          </div>
        </MetadataContainer>
      </div>
    </>
  );
};

export default VideoDetails;
